/**
 * snapshot の board 群の history 増分を watch して、 増えた手数 × 適切な kind で音を鳴らす。
 *
 * - 自分の手 / 相手の手 / CPU の手 すべて同じ経路 (= history が伸びる) で検出。
 * - 「自分の click 直後」 と「broadcast で snapshot が来た直後」 の二重発火は
 *   ply count が同じ手については 1 度だけ trigger するので発生しない (= 同じ ply は
 *   既に再生済みと記憶)。
 * - mount 直後の「履歴復元」 では音を鳴らさない (= reload で過去の全手の音が連続で
 *   鳴ったらうるさい)。 初回 ref 初期化時に現状の history.length を「再生済み」 と扱う。
 *
 * 「取り音」 検出は v1 では未実装 (= デフォルト kind で統一)。 v2 で move 内容
 * (e.g. `mv.Push.capture: true` 等) を見て capture を発火する。
 */

import { useEffect, useRef } from 'react';
import { playSound } from '../lib/audio';
import type { Snapshot } from '../lib/match-snapshot';
import { kindForMove } from '../lib/sound-map';

export function useMoveSound(snapshot: Snapshot | null): void {
  // board_id → 最後に観測した history.length。 増分が出たら音を鳴らす。
  const lastSeenRef = useRef<Map<number, number>>(new Map());
  // 初回かどうか (= mount 直後の reload 復元では音を鳴らさない)。
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!snapshot?.boards) return;
    if (!initializedRef.current) {
      // 初回: 現状の history.length を「再生済み」 として記録。 音は鳴らさない。
      for (const b of snapshot.boards) {
        lastSeenRef.current.set(b.board, b.history.length);
      }
      initializedRef.current = true;
      return;
    }
    for (const b of snapshot.boards) {
      const prev = lastSeenRef.current.get(b.board) ?? 0;
      const cur = b.history.length;
      if (cur <= prev) {
        // 同じ ply か (まれに) ロールバック。 何もしない。
        lastSeenRef.current.set(b.board, cur);
        continue;
      }
      // 増えた手数だけ音を鳴らす (通常 1 手だが BG の連続着手や Poker の自動 NextHand で複数のことあり)。
      // 6 手連続を上限 (= 暴発防止)。
      const delta = Math.min(cur - prev, 6);
      for (let i = 0; i < delta; i++) {
        // **各 ply の mv を見て kind を個別判定** (= Poker の bet → chip, fold → fold,
        // 等を 1 連続の中で正しく鳴らし分けるため)。
        const entry = b.history[prev + i];
        const kind = kindForMove(b.game_id, entry?.mv);
        // 連打のときの間隔は ~50ms ずらして「ガガッ」 感を出す。
        const offsetMs = i * 50;
        if (offsetMs === 0) playSound(kind);
        else setTimeout(() => playSound(kind), offsetMs);
      }
      lastSeenRef.current.set(b.board, cur);
    }
  }, [snapshot]);
}
